import React, { useEffect, useState } from 'react';
// Third party
import axios from 'axios';
// Components
import { PaginationNav, SearchTable } from './';
import Table from '../Structures/Table';
import { Link, useLocation, useParams } from 'react-router-dom';
// Icons
import { IoCalendarClearOutline } from 'react-icons/io5';
import { TbClockPlay } from 'react-icons/tb';
import { LuLoader2 } from 'react-icons/lu';
// store
import { useDispatch, useSelector } from 'react-redux';
// utils
import { formatTime, getChamberLabel } from '../../utils';
import { setFilterCommittee } from '../../features/video/videoSlice';
import PulsingIcon from './PulsingIcon';

const LIVES_URL = `${process.env.REACT_APP_LIVES_SERVER_URL || 'https://live-gateway.azurewebsites.net/'}app/`;

const HomeTable = ({ searchRes, loading, setLoading }) => {
  const dispatch = useDispatch();
  const { state } = useParams();
  const [videoList, setVideoList] = useState([]);
  const [livesList, setLivesList] = useState([]);
  const [awaitingList, setAwaitingList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const committeeQuery = queryParams.get('committee');

  const headersVideo = ['Date', 'Title', 'Chamber', 'Duration'];

  const rowsLives = (livesList || []).map((live, index) => {
    if (!live || typeof live !== 'object') {
      // Fallback if 'live' is null, undefined, or not an object
      return [
        <span key={`nodata-${index}`}>No data</span>,
        <span>No data</span>,
        <span>No data</span>,
        <span>No data</span>,
      ];
    }

    // Safely parse the date
    let dateText = 'Unknown Date';
    if (live.StartTime) {
      try {
        dateText = new Date(live.StartTime).toLocaleDateString('en-US', {
          timeZone: 'UTC',
        });
      } catch (err) {
        console.error('Error parsing live.StartTime:', err);
      }
    }

    const linkId = live.Id ?? `live-${index}`;
    const linkTitle = live.Title ?? 'No Title';
    const isOnLive = !!live.IsOnLive; // Convert truthy/falsy to boolean
    const chamberText = getChamberLabel(live.Chamber);

    return [
      // Date column
      <span className="flex items-center gap-2" key={`date-${index}`}>
        <IoCalendarClearOutline className="text-gray-400 w-4 h-4" />
        {dateText}
      </span>,

      // Title link column
      <Link key={`link-${index}`} to={`/${state}/videos/streaming/${linkId}`}>
        {linkTitle}
      </Link>,

      // Chamber column
      chamberText,

      // OnLive/Archived column
      isOnLive ? (
        <PulsingIcon key={`pulsing-${index}`} />
      ) : (
        <span className="flex items-center" key={`archived-${index}`}>
          archived
        </span>
      ),
    ];
  });

  // Sessions that ended but are still being archived; skip any the live list already shows.
  const liveIds = new Set((livesList || []).map((l) => String(l?.Id)));
  const rowsAwaiting = (awaitingList || [])
    .filter((item) => item && !liveIds.has(String(item.Id)))
    .map((item, index) => {
      let dateText = 'Unknown Date';
      if (item.StartTime) {
        const d = new Date(item.StartTime);
        if (!Number.isNaN(d.getTime())) dateText = d.toLocaleDateString('en-US', { timeZone: 'UTC' });
      }
      return [
        <span className="flex items-center gap-2" key={`aw-date-${index}`}>
          <IoCalendarClearOutline className="text-gray-400 w-4 h-4" />
          {dateText}
        </span>,
        <Link key={`aw-link-${index}`} to={`/${state}/videos/streaming/${item.Id}`}>
          {item.Title ?? 'No Title'}
        </Link>,
        getChamberLabel(item.Chamber),
        <span className="flex items-center gap-1 text-warning-strong" key={`aw-status-${index}`}>
          <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          processing
        </span>,
      ];
    });

  const rowsVideo = videoList.map((videoInfo) => [
    <span className="flex items-center gap-2">
      <IoCalendarClearOutline className="text-gray-400 w-4 h-4" />
      {new Date(videoInfo.Date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
    </span>,
    <Link key={videoInfo.VideoId} to={`/${state}/video/${videoInfo.VideoId}`}>
      {videoInfo.Committee}
    </Link>,
    getChamberLabel(videoInfo.Chamber),
    <span className="flex items-center gap-1">
      <TbClockPlay className="text-gray-400 w-5 h-5" />
      {formatTime(videoInfo.Duration)}
    </span>,
  ]);

  const filters = useSelector((state) => state.video.filter);
  const isFiltering = filters.date || filters.committee || filters.legislature || filters.chamber;

  useEffect(() => {
    if (!searchRes) getVideoList();
  }, [page, searchRes, filters]);

  useEffect(() => {
    if (committeeQuery) dispatch(setFilterCommittee(committeeQuery));
  }, []);

  const getVideoList = async () => {
    try {
      setLoading(true);

      let api_url = `/api/video?page=${page}&pageSize=${20}`;

      if (filters?.date) {
        api_url = api_url + `&month=${filters?.date.getMonth() + 1}&year=${filters?.date.getFullYear()}`;
      }

      if (filters?.chamber) api_url += `&chamber=${filters?.chamber}`;

      if (filters?.committee || committeeQuery) api_url += `&committee=${filters?.committee || committeeQuery}`;

      if (filters?.legislature) api_url += `&legislature=${filters?.legislature}`;

      const response = await axios.get(api_url);
      setVideoList(response.data.data);

      // CHANGE THIS TO MAKE IT FLEXIBLE

      try {
        const plainAxios = axios.create({});
        const [responseLives, responseAwaiting] = await Promise.allSettled([
          plainAxios.get(`${LIVES_URL}${state}/live`),
          plainAxios.get(`${LIVES_URL}${state}/live/awaiting-archive`),
        ]);
        if (responseLives.status === 'fulfilled') setLivesList(responseLives.value.data.data);
        else console.error(responseLives.reason);
        setAwaitingList(responseAwaiting.status === 'fulfilled' ? responseAwaiting.value.data?.data || [] : []);
      } catch (error) {
        console.error(error);
      }

      setTotalPages(response.data.pagination.totalPages);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col gap-3 justify-between relative">
      <div className="rounded-xl text-gray-500 ">
        {!searchRes ? (
          <Table
            headers={headersVideo}
            rows={page === 1 && !isFiltering ? [...rowsLives, ...rowsAwaiting, ...rowsVideo] : rowsVideo}
            loading={loading}
          />
        ) : (
          <SearchTable searchList={searchRes || []} />
        )}
      </div>
      {searchRes?.length < 1 && (
        <span className="text-xl mt-5 text-gray-500">I'm sorry, we didn't find any video...</span>
      )}
      {!loading && !searchRes && (
        <div className="w-full flex justify-end">
          <PaginationNav selected={page} setSelected={setPage} totalPages={totalPages} />
        </div>
      )}
    </div>
  );
};

export default HomeTable;
